import { FormikErrors, FormikHelpers, useFormik } from 'formik';
import { chain } from 'lodash';
import { useCallback, useMemo } from 'react';
import { v4 as uuuidv4 } from 'uuid';
import { ComboRequestGq } from '../../../../../../../../../graphql/projects/models/ComboRequestGq';
import { ProjectGq } from '../../../../../../../../../graphql/projects/models/ProjectGq';
import {
  ComboCombinationInputGq,
  WordGroupInputGq,
} from '../../../../../../../../../graphql/projects/mutations/googleAdsCombo/useUpdateComboRequestGq';
import { projectsApiService } from '../../../../../../../../../graphql/projects/projectsApiService';
import { FormCombination, FormWordGroup } from '../models/ComboFormState';
import { ComboFormState } from '../models/ComboFormState';
import { getCombinationName } from './getCombinationName';
import { useProjectDefaults } from '../../../../../../../../../logic/otherUtilities/useProjectDefaults';

export function useSetUpComboForm(project: ProjectGq, comboRequest?: ComboRequestGq) {
  const defaults = useProjectDefaults(project);
  // Initial values
  const initialValues: ComboFormState = useMemo(() => {
    const result: ComboFormState = {
      id: comboRequest?.id ?? 0,
      name: comboRequest?.googleAdsRequest.name ?? '',
      includeZeroVolume: comboRequest?.googleAdsRequest?.minSearchVolume == 0,
      language: defaults.volumeLanguage,
      country: defaults.volumeCountry,
      city: defaults.volumeCity,
      includeMonths: comboRequest?.googleAdsRequest.includeMonths ?? project.defaultIncludeMonths,
      useComboTagging: comboRequest?.googleAdsRequest?.useSeedSpecificTagging ?? true,
      wordGroups: [],
      comboCombinations: [],
    };

    // Set word groups
    if (comboRequest?.wordGroups?.length && comboRequest?.comboCombinations.length) {
      result.wordGroups = chain(comboRequest.wordGroups)
        .map(
          (wg): FormWordGroup => ({
            name: wg.name,
            order: wg.order,
            id: wg.id.toString(),
            words: wg.wordItems ?? '',
          })
        )
        .orderBy((wg) => wg.order)
        .value();
    } else {
      result.wordGroups = [
        { name: 'A', order: 0, id: '1', words: '' },
        { name: 'B', order: 1, id: '2', words: '' },
      ];
    }

    // Set combinations
    if (comboRequest?.wordGroups?.length && comboRequest?.comboCombinations.length) {
      result.comboCombinations = comboRequest.comboCombinations.map((cc): FormCombination => {
        const combination: FormCombination = {
          id: uuuidv4(),
          selected: cc.selected,
          wordGroups: chain(cc.wordGroupIds)
            .map((CCwg, index) => ({ id: CCwg, order: index }))
            .value(),
          name: '',
          removeSpacing: cc.removeSpacing ? cc.removeSpacing : 0,
        };

        combination.name = getCombinationName(combination, result.wordGroups);

        return combination;
      });
    } else {
      result.comboCombinations = [
        {
          id: uuuidv4(),
          selected: false,
          wordGroups: [{ id: '1', order: 0 }],
          name: 'A',
          removeSpacing: 0,
        },
        {
          id: uuuidv4(),
          selected: false,
          wordGroups: [{ id: '2', order: 0 }],
          name: 'B',
          removeSpacing: 0,
        },
        {
          id: uuuidv4(),
          selected: false,
          wordGroups: [
            { id: '1', order: 0 },
            { id: '2', order: 1 },
          ],
          name: 'A B',
          removeSpacing: 0,
        },
        {
          id: uuuidv4(),
          selected: false,
          wordGroups: [
            { id: '1', order: 0 },
            { id: '2', order: 1 },
          ],
          name: 'AB',
          removeSpacing: 1,
        },
        {
          id: uuuidv4(),
          selected: false,
          wordGroups: [
            { id: '2', order: 0 },
            { id: '1', order: 1 },
          ],
          name: 'B A',
          removeSpacing: 0,
        },
        {
          id: uuuidv4(),
          selected: false,
          wordGroups: [
            { id: '2', order: 0 },
            { id: '1', order: 1 },
          ],
          name: 'BA',
          removeSpacing: 1,
        },
      ];
    }

    return result;
  }, [comboRequest?.id]); // Only reinitialize upon first reset

  const validationFn = useCallback((values: ComboFormState) => {
    const errors: FormikErrors<ComboFormState> = {};
    if (values.wordGroups.some((wg) => wg.words.split(/[\n,]+/g).length > 500)) {
      const wordGroupNames = values.wordGroups
        .filter((wg) => wg.words.split(/[\n,]+/g).length > 500)
        .map((w) => w.name)
        .join(', ');
      errors.wordGroups = `More than 500 words is not allowed in one word group. Error word group: ${wordGroupNames}`;
    }
    return errors;
  }, []);

  // Submit
  const updateMutation = projectsApiService.mutations.googleAdsCombo.useUpdateComboList();
  const submitFn = useCallback(
    (values: ComboFormState, helpers: FormikHelpers<ComboFormState>) =>
      updateMutation({
        id: values.id,
        name: values.name,
        projectId: project.id,
        minSearchVolume: values.includeZeroVolume ? 0 : 1,
        languageId: values.language?.id,
        languageName: values.language?.name,
        locationId: !!values.city?.id ? values.city?.id : values.country?.id,
        locationName: !!values.city?.id ? values.city?.name : values.country?.name,
        includeMonths: values.includeMonths,
        useComboTagging: values.useComboTagging,
        wordGroups: values.wordGroups.map(
          (wg): WordGroupInputGq => ({
            id: wg.id,
            order: wg.order,
            name: wg.name,
            words: wg.words
              .split(/[\n,]+/g)
              .map((w) => w.trim())
              .filter((w) => w?.length)
              .join('\n'),
          })
        ),
        comboCombinations: values.comboCombinations.map(
          (cc): ComboCombinationInputGq => ({
            selected: cc.selected,
            wordGroups: cc.wordGroups.map((wg) => wg.id),
            removeSpacing: cc.removeSpacing,
          })
        ),
      })
        .catch((error) => console.error(error))
        .then(() => helpers.resetForm({ values })),

    []
  );

  const formBag = useFormik<ComboFormState>({
    enableReinitialize: true,
    initialValues: initialValues,
    validate: validationFn,
    onSubmit: submitFn,
  });

  return formBag;
}
